import { nanoid } from 'nanoid/non-secure';

/**
 * Generic concurrent upload queue with bounded parallelism + event
 * distribution. Web port of PopshopRN's `MediaFileUploadController`
 * (`@psl/functions/media/MediaFileUploadController`).
 *
 * The controller is a long-lived singleton owned by the feature
 * context (e.g. the community-channels provider holds one for
 * channel-message attachments). Construct once with a
 * `MediaFileService` that knows how to upload a single file —
 * request presigned URL, S3 PUT, etc. — and let the controller
 * handle the queue, concurrency, abort, and the listener fan-out.
 *
 * Two listener channels:
 * - `addListener(...)` — broadcast: every event for every task. Used
 *   by the bridge hook that patches `medias$` so the UI can render
 *   per-message progress.
 * - `request.listener` — per-task: receives only that task's events,
 *   delivered in the microtask queue so synchronous `emit` chains
 *   (`queued` → `begin` → `progress`) flush in order regardless of
 *   when the listener was registered.
 */

export type MediaUploadTaskEvent<TResult extends MediaFileUploadResponse> =
  | { type: 'queued' }
  | { type: 'begin' }
  | { type: 'progress'; progress: number }
  | { type: 'error'; error: Error }
  | { type: 'finished'; result: TResult }
  | { type: 'abort'; error?: Error };

export type MediaUploadEventListener<
  TResult extends MediaFileUploadResponse,
  TContext = unknown,
> = (
  event: MediaUploadTaskEvent<TResult>,
  task: MediaUploadTask<TResult, TContext>,
) => void;

export interface MediaUploadRequest<
  TResult extends MediaFileUploadResponse,
  TContext = unknown,
> {
  /** Opaque consumer state — the bridge hook keys `medias$` entries off this. */
  context?: TContext;
  type: 'image' | 'video';
  /** Object URL (`URL.createObjectURL(file)`) for preview rendering. */
  localFileUri: string;
  /** The actual `File` to upload — passed through to the file service. */
  localFile?: File;
  filename: string;
  contentType: string;
  /** Free-form metadata threaded through to the file service. */
  metadata?: Record<string, unknown>;
  /** Per-task listener (broadcast listeners receive every task). */
  listener?: MediaUploadEventListener<TResult, TContext>;
}

export interface MediaUploadTask<
  TResult extends MediaFileUploadResponse,
  TContext = unknown,
> {
  key: string;
  status: 'queued' | 'inprogress' | 'complete' | 'aborted';
  request: MediaUploadRequest<TResult, TContext>;
  abortController: AbortController;
}

export interface MediaFileUploadResponse {
  id: string;
  url: string;
}

export interface MediaFileUploadParameters {
  filename: string;
  localFileUri: string;
  contentType: string;
  localFile?: File;
  metadata?: Record<string, unknown>;
  abortController: AbortController;
  onProgress: (progress: number) => void;
}

export interface MediaFileService<
  TResult extends MediaFileUploadResponse = MediaFileUploadResponse,
> {
  upload: (parameters: MediaFileUploadParameters) => Promise<TResult>;
}

const kDefaultConcurrency = 10;

export class MediaFileUploadController<
  TResult extends MediaFileUploadResponse = MediaFileUploadResponse,
  TContext = unknown,
> {
  private readonly concurrency: number;
  private readonly mediaFileService: MediaFileService<TResult>;
  private readonly queue: MediaUploadTask<TResult, TContext>[] = [];
  private readonly inProgressTasks = new Map<
    string,
    MediaUploadTask<TResult, TContext>
  >();
  private readonly listeners: MediaUploadEventListener<TResult, TContext>[] =
    [];

  constructor(
    mediaFileService: MediaFileService<TResult>,
    options?: { concurrency?: number },
  ) {
    this.concurrency = options?.concurrency ?? kDefaultConcurrency;
    this.mediaFileService = mediaFileService;
  }

  /**
   * Subscribe to every event for every task. Returns the unsubscribe
   * function. The bridge hook (`useChannelMessageMediaUploader`)
   * uses this to mirror the queue into `medias$`.
   */
  addListener(
    listener: MediaUploadEventListener<TResult, TContext>,
  ): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index >= 0) this.listeners.splice(index, 1);
    };
  }

  /**
   * Add a task to the queue. Emits `queued` synchronously and kicks
   * off execution if the in-progress slot count is below the
   * concurrency cap. Returns the assigned `key` so the caller can
   * `abort(key)` later.
   */
  enqueue(request: MediaUploadRequest<TResult, TContext>): string {
    const task: MediaUploadTask<TResult, TContext> = {
      key: nanoid(),
      status: 'queued',
      request,
      abortController: new AbortController(),
    };
    this.queue.push(task);
    this.emit({ type: 'queued' }, task);
    this.next();
    return task.key;
  }

  /**
   * Cancel a queued or in-flight task. Returns `true` if a task was
   * found (queued tasks emit `abort` immediately; in-progress tasks
   * signal abort to the file service and emit `abort` once the
   * service's promise rejects).
   */
  abort(key: string): boolean {
    const inProgress = this.inProgressTasks.get(key);
    if (inProgress) {
      inProgress.status = 'aborted';
      inProgress.abortController.abort();
      return true;
    }
    const index = this.queue.findIndex(task => task.key === key);
    if (index >= 0) {
      const task = this.queue.splice(index, 1)[0];
      task.status = 'aborted';
      this.emit({ type: 'abort' }, task);
      return true;
    }
    return false;
  }

  /**
   * Drop all listeners and abort every queued / in-flight task. Used
   * when the owning feature unmounts.
   */
  dispose(): void {
    this.listeners.splice(0, this.listeners.length);
    for (const task of this.inProgressTasks.values()) {
      task.request = { ...task.request, listener: undefined };
      task.abortController.abort();
    }
    this.inProgressTasks.clear();
    this.queue.splice(0, this.queue.length);
  }

  private next(): void {
    if (this.inProgressTasks.size >= this.concurrency) return;
    const task = this.queue.shift();
    if (!task) return;
    void this.exec(task);
  }

  private emit(
    event: MediaUploadTaskEvent<TResult>,
    task: MediaUploadTask<TResult, TContext>,
  ): void {
    for (const listener of this.listeners) {
      listener(event, task);
    }
    // Defer per-task listener to the microtask queue so synchronous
    // emit chains (`queued` → `begin` → `progress`) flush in order
    // regardless of when the request's listener was registered.
    queueMicrotask(() => {
      task.request.listener?.(event, task);
    });
  }

  private async exec(task: MediaUploadTask<TResult, TContext>): Promise<void> {
    if (task.abortController.signal.aborted) {
      task.status = 'aborted';
      this.next();
      return;
    }

    this.inProgressTasks.set(task.key, task);
    task.status = 'inprogress';
    this.emit({ type: 'begin' }, task);

    try {
      const result = await this.mediaFileService.upload({
        filename: task.request.filename,
        localFileUri: task.request.localFileUri,
        localFile: task.request.localFile,
        contentType: task.request.contentType,
        metadata: task.request.metadata,
        abortController: task.abortController,
        onProgress: progress => {
          this.emit({ type: 'progress', progress }, task);
        },
      });
      this.emit({ type: 'finished', result }, task);
    } catch (error) {
      const isAbort =
        task.abortController.signal.aborted ||
        (error instanceof Error && error.name === 'AbortError');
      if (isAbort) {
        this.emit(
          { type: 'abort', error: error instanceof Error ? error : undefined },
          task,
        );
      } else {
        this.emit(
          {
            type: 'error',
            error: error instanceof Error ? error : new Error(String(error)),
          },
          task,
        );
      }
    } finally {
      task.status = 'complete';
      this.inProgressTasks.delete(task.key);
      this.next();
    }
  }
}
